import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../api/client';
import type { AccountDto, CreateAccountRequest, UpdateAccountRequest } from './types';

const QK_ROOT = ['accounts'] as const;
const QK = (includeInactive: boolean) => [...QK_ROOT, includeInactive] as const;

export function useAccounts(includeInactive: boolean) {
  return useQuery({
    queryKey: QK(includeInactive),
    queryFn: async () =>
      (
        await apiClient.get<AccountDto[]>('/api/accounting/accounts', {
          params: { includeInactive },
        })
      ).data,
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: CreateAccountRequest) =>
      (await apiClient.post<AccountDto>('/api/accounting/accounts', request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_ROOT }),
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, request }: { id: string; request: UpdateAccountRequest }) =>
      (await apiClient.put<AccountDto>(`/api/accounting/accounts/${id}`, request)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_ROOT }),
  });
}

export interface ImportAccountRow {
  code: string;
  nameAr: string;
  nameEn?: string | null;
  parentCode: string;
  currencyCode?: string | null;
}

export function useImportChart() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rows: ImportAccountRow[]) =>
      (await apiClient.post<{ created: number }>('/api/accounting/accounts/import', { rows })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_ROOT }),
  });
}

export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/accounting/accounts/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_ROOT }),
  });
}
